import { Observable } from 'rxjs';
import { singletonFactory } from '@warehouse/shared/util';
import {
	AssignTitlesParams,
	CreateFolderParams,
	CreateFolderResponse,
	Filters,
	FindNxObjectsByETagsParams,
	FindNxObjectsByUuidsParams,
	InfraResourceIdentifier,
	NxObject,
	NxObjectFull,
	NxObjectRepository,
	SearchMode,
	WatchGetNxObjectResult,
	WatchNxObjectParams,
	WatchNxObjectsParams,
	WatchTreeParams,
} from '@warehouse/object-browser/core';
import { PaginatedDocuments } from '@warehouse/shared/core';
import { inventoryFetchClientAdapter } from './nx-object-fetch-client';
import { NxObjectAdapter } from './nx-object.adapter';
import { NxObjectFiltersBuilder } from './nx-object-filters-builder';
import { NxObjectHashBuilder } from './nx-object-hash-builder';
import { WildCardSearchAdapter } from './wild-card-search.adapter';
import { NxObjectFullAdapter } from './nx-object-full.adapter';
import { OrderByAdapter } from './order-by.adapter';
import { collectAllPages } from './collect-all-pages';

/** Copies of one piece of content are usually few; a page this size covers a whole Service Log page of eTags. */
const COPIES_PAGE_SIZE = 500;

export class NxObjectRepositoryHttp implements NxObjectRepository {
	#inventoryFetchClientAdapter = inventoryFetchClientAdapter;

	watchNxObject({ nxObjectUuid, options }: WatchNxObjectParams): Observable<WatchGetNxObjectResult> {
		return this.#inventoryFetchClientAdapter.watchObservableQuery({
			queryKey: NxObjectHashBuilder.createGetQueryHash({ nxObjectUuid }),
			queryFn: () => this.#getNxObject({ nxObjectUuid }),
			refetchInterval: options?.pollInterval,
		});
	}

	watchNxObjects({
		filters = { pagination: { page: 1, perPage: 50 } },
		options,
		resourceIdentifier,
		searchMode,
		assignedTitleUuid,
	}: WatchNxObjectsParams) {
		return this.#inventoryFetchClientAdapter.watchObservableQuery({
			queryKey: NxObjectHashBuilder.createSearchQueryHash({
				filters,
				resourceIdentifier,
				searchMode,
				assignedTitleUuid,
			}),
			queryFn: () => this.#searchNxObject({ filters, resourceIdentifier, searchMode, assignedTitleUuid }),
			refetchInterval: options?.pollInterval,
		});
	}

	async assignTitles({ nxObjectUuid, titleUuids }: AssignTitlesParams): Promise<NxObjectFull> {
		const response = await this.#inventoryFetchClientAdapter.client.PUT('/nx-object/{uuid}/assign-titles', {
			params: {
				path: {
					uuid: nxObjectUuid,
				},
			},
			body: {
				titleUuids,
			},
		});

		if (response.error) throw new Error('Error assigning titles');
		return NxObjectFullAdapter.adapt(response.data);
	}

	async createFolder({ folderName, bucket, parentPath }: CreateFolderParams): Promise<CreateFolderResponse> {
		const { error, data } = await this.#inventoryFetchClientAdapter.client.POST('/nx-objects/create-folder', {
			body: {
				folderName,
				bucket,
				...(parentPath && { parentPath }),
			},
		});

		if (error) {
			const errorMessage = error?.message || 'Error creating folder';
			throw new Error(errorMessage);
		}
		return data as CreateFolderResponse;
	}

	async #getNxObject({ nxObjectUuid }: { nxObjectUuid: string }): Promise<NxObjectFull> {
		const { error, data } = await this.#inventoryFetchClientAdapter.client.GET('/nx-object/{uuid}', {
			params: {
				path: {
					uuid: nxObjectUuid,
				},
			},
		});
		if (error) throw new Error('Error fetch nx object');

		return NxObjectFullAdapter.adapt(data);
	}

	async #searchNxObject({
		filters = { pagination: { page: 1, perPage: 50 } },
		resourceIdentifier,
		searchMode,
		assignedTitleUuid,
	}: {
		filters?: Filters;
		resourceIdentifier?: InfraResourceIdentifier;
		searchMode?: SearchMode;
		assignedTitleUuid: string | undefined;
	}): Promise<PaginatedDocuments<NxObject>> {
		const { error, data } = await this.#inventoryFetchClientAdapter.client.POST('/nx-objects/search', {
			body: {
				orderBy: OrderByAdapter.adapt(filters.sort),
				pagination: filters.pagination,
				filters: NxObjectFiltersBuilder.buildFilters({
					filters: filters.filter,
					resourceIdentifier,
					searchMode,
					assignedTitleUuid,
				}),
				wildcardSearch: WildCardSearchAdapter.adapt(filters.textSearch),
			},
		});

		if (error) throw new Error('Error fetching nx objects');

		return {
			documents: NxObjectAdapter.adaptArray(data.documents),
			pagination: data.page,
		};
	}

	async findNxObjectsByUuids({ uuids }: FindNxObjectsByUuidsParams): Promise<NxObject[]> {
		if (uuids.length === 0) return [];
		return this.#findNxObjects({ field: 'uuid', values: uuids, perPage: uuids.length });
	}

	async findNxObjectsByETags({ eTags }: FindNxObjectsByETagsParams): Promise<NxObject[]> {
		if (eTags.length === 0) return [];
		return this.#findNxObjects({ field: 'eTag', values: eTags, perPage: COPIES_PAGE_SIZE });
	}

	/**
	 * One search, no wildcard, no sort: the lookup behind the Service Log's Object and Bucket columns. Every page is
	 * read, so content with more copies than a page holds can never crowd the other eTags out of the result.
	 */
	async #findNxObjects({
		field,
		values,
		perPage,
	}: {
		field: 'uuid' | 'eTag';
		values: string[];
		perPage: number;
	}): Promise<NxObject[]> {
		const documents = await collectAllPages(async (page) => {
			const { error, data } = await this.#inventoryFetchClientAdapter.client.POST('/nx-objects/search', {
				body: {
					pagination: { page, perPage },
					filters: { and: [{ field, operator: 'isAnyOf', value: values }] },
				},
			});

			if (error) throw new Error('Error fetching nx objects');

			return { documents: data.documents, totalPage: data.page.totalPage };
		});

		return NxObjectAdapter.adaptArray(documents);
	}

	watchTree({ resourceIdentifier, options }: WatchTreeParams) {
		return this.watchNxObjects({
			filters: {
				filter: {
					combinationOperator: 'AND',
					filters: [
						{ field: 'itemType', operator: 'equals', value: 'folder' },
						{ field: 'online', operator: 'equals', value: true },
					],
				},
				sort: [{ field: 'name', sort: 'asc' }],
				pagination: {
					page: 1,
					perPage: 5000,
				},
			},
			options,
			resourceIdentifier,
		});
	}
}

export const nxObjectRepositorySingleton = singletonFactory<NxObjectRepository>({
	factory: () => new NxObjectRepositoryHttp(),
});
